import React, { useState } from 'react';
import { Nav, Footer, DisclaimerBar } from '../components/Chrome';
import { useI18n } from '../i18n';
import { api } from '../api';
import { useRecaptcha } from '../hooks/useRecaptcha';

const PRODUCT_TO_LOOKUP = {
  deal_decision_report: 'pythia_standard',
  rush: 'pythia_rush',
  premium_memo: 'pythia_rush',
};
const PRODUCT_TO_PRICE = {
  deal_decision_report: 100,
  rush: 799,
  premium_memo: 799,
};

export default function Intake() {
  const { t } = useI18n();
  const executeRecaptcha = useRecaptcha();
  const [form, setForm] = useState({
    client_name: '', client_email: '', client_phone: '',
    address: '', folio: '', asking_price: '',
    intended_use: 'buy_hold', buyer_profile: '', timeline: '', known_risks: '',
    mls_authorization: false, target_decision: '', product: 'deal_decision_report',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState('');
  const [payWithCard, setPayWithCard] = useState(true);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const recaptcha_token = await executeRecaptcha('intake_submit');
      const payload = { ...form, asking_price: form.asking_price ? Number(form.asking_price) : null, recaptcha_token };
      const res = await api.post('/intake', payload);
      const intakeId = res.data.id;

      if (payWithCard) {
        // Kick off Stripe Checkout
        const lookupKey = PRODUCT_TO_LOOKUP[form.product] || 'pythia_standard';
        const checkoutRes = await api.post('/payments/checkout', {
          lookup_key: lookupKey,
          origin_url: window.location.origin,
          intake_id: intakeId,
          client_email: form.client_email,
        });
        window.location.href = checkoutRes.data.checkout_url;
        return;
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail || 'Error');
    }
  };

  const Section = ({ n, title, children }) => (
    <div className="border hairline bg-surface1">
      <div className="border-b hairline px-6 py-3 flex items-center gap-4">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ {n}</div>
        <div className="mono text-[11px] uppercase tracking-widest2 text-white/80">{title}</div>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  const Field = ({ label, children }) => (
    <label className="flex flex-col gap-2">
      <span className="mono text-[10px] uppercase tracking-widest2 text-white/50">{label}</span>
      {children}
    </label>
  );

  const inputCls = "bg-obsidian border hairline px-3 py-2.5 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none";

  if (status === 'success') {
    return (
      <div>
        <Nav />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="mono text-[10px] uppercase tracking-widest2 text-gold">RECEIVED</div>
          <h1 className="serif text-5xl md:text-6xl mt-6">{t('intake.success')}</h1>
          <p className="mt-6 text-white/60 max-w-xl mx-auto">Your intake ID is stored. We will confirm delivery within 4 business hours.</p>
        </div>
        <DisclaimerBar />
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">INTAKE FORM</div>
        <h1 className="serif text-5xl md:text-6xl leading-tight max-w-3xl">{t('intake.title')}</h1>
        <p className="mt-4 text-white/60 max-w-2xl">{t('intake.sub')}</p>

        <form onSubmit={submit} className="mt-12 space-y-4" data-testid="intake-form">
          <Section n="01" title={t('intake.section.contact')}>
            <Field label={t('intake.field.name')}>
              <input required className={inputCls} value={form.client_name} onChange={e=>update('client_name', e.target.value)} data-testid="intake-name" />
            </Field>
            <Field label={t('intake.field.email')}>
              <input required type="email" className={inputCls} value={form.client_email} onChange={e=>update('client_email', e.target.value)} data-testid="intake-email" />
            </Field>
            <Field label={t('intake.field.phone')}>
              <input className={inputCls} value={form.client_phone} onChange={e=>update('client_phone', e.target.value)} data-testid="intake-phone" />
            </Field>
          </Section>

          <Section n="02" title={t('intake.section.property')}>
            <Field label={t('intake.field.address')}>
              <input required className={inputCls} value={form.address} onChange={e=>update('address', e.target.value)} data-testid="intake-address" />
            </Field>
            <Field label={t('intake.field.folio')}>
              <input className={inputCls} value={form.folio} onChange={e=>update('folio', e.target.value)} data-testid="intake-folio" />
            </Field>
            <Field label={t('intake.field.asking')}>
              <input type="number" className={inputCls} value={form.asking_price} onChange={e=>update('asking_price', e.target.value)} data-testid="intake-asking" />
            </Field>
          </Section>

          <Section n="03" title={t('intake.section.intent')}>
            <Field label={t('intake.field.use')}>
              <select className={inputCls} value={form.intended_use} onChange={e=>update('intended_use', e.target.value)} data-testid="intake-use">
                {['buy_hold','fix_flip','primary','wholesale','listing_autopsy','other'].map(u => (
                  <option key={u} value={u}>{t(`intake.use.${u}`)}</option>
                ))}
              </select>
            </Field>
            <Field label={t('intake.field.profile')}>
              <input className={inputCls} value={form.buyer_profile} onChange={e=>update('buyer_profile', e.target.value)} data-testid="intake-profile" />
            </Field>
            <Field label={t('intake.field.timeline')}>
              <input className={inputCls} value={form.timeline} onChange={e=>update('timeline', e.target.value)} data-testid="intake-timeline" />
            </Field>
            <Field label={t('intake.field.target')}>
              <input className={inputCls} value={form.target_decision} onChange={e=>update('target_decision', e.target.value)} data-testid="intake-target" />
            </Field>
            <label className="md:col-span-2 flex flex-col gap-2">
              <span className="mono text-[10px] uppercase tracking-widest2 text-white/50">{t('intake.field.risks')}</span>
              <textarea rows={3} className={inputCls} value={form.known_risks} onChange={e=>update('known_risks', e.target.value)} data-testid="intake-risks" />
            </label>
            <label className="md:col-span-2 flex items-center gap-3 mono text-xs text-white/70">
              <input type="checkbox" checked={form.mls_authorization} onChange={e=>update('mls_authorization', e.target.checked)} data-testid="intake-mls" />
              {t('intake.field.mls')}
            </label>
          </Section>

          <Section n="04" title={t('intake.section.product')}>
            <Field label={t('intake.field.product')}>
              <select className={inputCls} value={form.product} onChange={e=>update('product', e.target.value)} data-testid="intake-product">
                <option value="deal_decision_report">{t('intake.product.deal_decision_report')}</option>
                <option value="rush">{t('intake.product.rush')}</option>
                <option value="premium_memo">{t('intake.product.premium_memo')}</option>
              </select>
            </Field>
            <div className="md:col-span-2 border-t hairline pt-4 mt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={payWithCard} onChange={e=>setPayWithCard(e.target.checked)} className="mt-1" data-testid="intake-paycard" />
                <span>
                  <span className="mono text-[11px] uppercase tracking-widest2 text-gold">{t('intake.pay.title')} — ${PRODUCT_TO_PRICE[form.product]}</span>
                  <div className="mt-1 text-xs text-white/60">{t('intake.pay.body')}</div>
                  <div className="mt-2 mono text-[10px] uppercase tracking-widest2 text-white/40">Stripe test mode · card 4242 4242 4242 4242 · any future date · any CVC</div>
                </span>
              </label>
            </div>
          </Section>

          {status === 'error' && <div className="text-red-400 mono text-xs">{errMsg}</div>}

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={status === 'sending'}
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white disabled:opacity-50"
              style={{transitionProperty: 'background-color'}}
              data-testid="intake-submit"
            >
              {status === 'sending' ? 'Processing…' : payWithCard ? t('intake.submit.pay') : t('intake.submit')} <span>→</span>
            </button>
          </div>
        </form>
      </div>
      <DisclaimerBar />
      <Footer />
    </div>
  );
}
